import {
  type AddEntryEventSource,
  getDatabaseContainerType,
  useAnalytics
} from '@databases/analytics';
import { collabStateManager } from '@databases/collab-state-manager';
import { InlineRovoPrompt } from '@databases/database-ai-first-creation-ui/InlineRovoPrompt';
import LayoutResizeObserver from '@databases/layout/observer/ResizeObserver';
import {
  useScrollPosition,
  useWindowScrollPosition
} from '@databases/layout/scroll-position';
import { isNativeEmbed, isNativeEmbedEditorPlugin } from '@databases/modes';
import { NativeEmbedSelectionBorder } from '@databases/native-embed-editor-plugin/SelectionBorder';
import { getPinnedLeadingAddEntryScrollLeft } from '@databases/pinned-columns/pinned-leading-add-entry-scroll';
import { useResizeObserver } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getService } from '@databases/service-container';
import { type TimelineLayoutProps } from '@databases/timeline-layout';
import { type DatabaseEntry } from '@databases/types/database';
import {
  type DatabaseViewSettings,
  DatabaseLayoutType
} from '@databases/types/database/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import useView from '@databases/views/useView';
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { manuallyFireExposureEvent } from '@canvas/client-feature-flags/experiments';
import { dynamicImport } from '@canvas/dynamic-import';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';

import { DatabaseBoardSkeleton } from '../database-skeletons/DatabaseBoardSkeleton.tsx';
import { DatabaseGallerySkeleton } from '../database-skeletons/DatabaseGallerySkeleton.tsx';
import { DatabaseTableSkeletonRefresh } from '../database-skeletons/DatabaseTableSkeletonRefresh.tsx';

import { DatabaseAiConfigModalProvider } from './ai-config/database-ai-config-modal-provider.tsx';
import styles from './database-layout-renderer.module.css';
import { DatabaseTableSkeleton } from './DatabaseContentSkeleton.tsx';
import DatabaseContentWrapper from './DatabaseContentWrapper.ts';
import {
  ProgressiveEntryRevealProvider,
  useProgressivelyRevealedEntryIdSet
} from './hooks/progressive-entry-reveal-context.tsx';
import {
  type ProgressiveEntryRevealLifecycleEvent,
  useProgressiveEntryReveal
} from './hooks/use-progressive-entry-reveal.ts';

/** While scrolling, render the rows in batches of this height, rather than rendering each row on its own. */
const RENDER_BATCH_HEIGHT = 500;
/** Render rows this amount of pixels above and below the visible section as well. */
const RENDER_MARGIN = 800;
/** Tolerance in pixels when comparing table width to container width, to prevent redundant scroll bar. */
const SCROLL_WIDTH_TOLERANCE_PX = 4;

const TableLayout = lazy(() =>
  dynamicImport(() => import('./table-layout/TableLayout.tsx'))
);
const BoardLayout = lazy(() =>
  dynamicImport(() => import('./board-layout/BoardLayout.tsx'))
);
const CalendarLayout = lazy(() =>
  dynamicImport(() => import('./calendar-layout/CalendarLayout.tsx'))
);
const GalleryLayout = lazy(() =>
  dynamicImport(() => import('./gallery-layout/GalleryLayout.tsx'))
);
const TimelineLayout = lazy(() =>
  dynamicImport(() => import('@databases/timeline-layout'))
);

type EditingIds = Set<{ entryId: string; fieldId: string | undefined }>;

function getProgressiveEntryRevealAnalyticsSource(
  layoutType: DatabaseLayoutType
): AddEntryEventSource | undefined {
  switch (layoutType) {
    case DatabaseLayoutType.TABLE:
      return 'tableLayout';
    case DatabaseLayoutType.BOARD:
      return 'boardLayout';
    case DatabaseLayoutType.GALLERY:
      return 'galleryLayout';
    case DatabaseLayoutType.TIMELINE:
      return 'timelineLayout';
  }

  return undefined;
}

function ProgressiveEntryRevealOwner({
  analyticsSource,
  children,
  contentId,
  editingIds,
  viewId,
  viewSettings
}: {
  analyticsSource: AddEntryEventSource;
  children: React.ReactNode;
  contentId: string;
  editingIds: EditingIds;
  viewId: string;
  viewSettings: DatabaseViewSettings;
}) {
  const subscribeToCollabManager = useCallback(
    (onStoreChange: () => void) =>
      collabStateManager.onCollabManagerAttached(contentId, onStoreChange),
    [contentId]
  );
  const getCollabManagerSnapshot = useCallback(
    () => collabStateManager.getCollabManager(contentId),
    [contentId]
  );
  const collabManager = useSyncExternalStore(
    subscribeToCollabManager,
    getCollabManagerSnapshot,
    () => undefined
  );
  const visibleEntryIds = defaultStoreUtils.useEqualSelector((state) => {
    const ignoredEntries = Array.from(editingIds).map(({ entryId }) => entryId);
    const database = selectDatabaseDraftByContentId(state, {
      contentId,
      viewSettings,
      includeDeleted: true,
      ignoredEntries
    });

    return database.entries;
  });
  const handleEligibleBackendAgentEntryAddition = useCallback(() => {
    manuallyFireExposureEvent('databaseBEStreaming');
  }, []);
  const analyticsClient = useAnalytics();
  const handleProgressiveEntryRevealLifecycleEvent = useCallback(
    (event: ProgressiveEntryRevealLifecycleEvent) => {
      void analyticsClient.sendDatabaseBEStreamingRevealEvent({
        ...event,
        source: analyticsSource,
        contentId,
        databaseContainer: getDatabaseContainerType()
      });
    },
    [analyticsClient, analyticsSource, contentId]
  );
  const progressiveEntryReveal = useProgressiveEntryReveal({
    collabManager,
    entryIds: visibleEntryIds,
    onEligibleBackendAgentEntryAddition: handleEligibleBackendAgentEntryAddition,
    onLifecycleEvent: handleProgressiveEntryRevealLifecycleEvent,
    resetKey: `${contentId}:${viewId}`
  });

  return (
    <ProgressiveEntryRevealProvider result={progressiveEntryReveal}>
      {children}
    </ProgressiveEntryRevealProvider>
  );
}

function ProgressiveTimelineLayout(props: Omit<TimelineLayoutProps, 'revealedEntryIds'>) {
  const revealedEntryIds = useProgressivelyRevealedEntryIdSet();

  return <TimelineLayout {...props} revealedEntryIds={revealedEntryIds} />;
}

const DatabaseLayoutRenderer = ({
  canEditContent,
  couldEditFields,
  canEditFields,
  contentId,
  isContentEditable = false,
  outerContainerRef,
  setOuterContainerRef
}: {
  contentId: string;
  canEditContent: boolean;
  couldEditFields: boolean;
  canEditFields: boolean;
  isContentEditable?: boolean;
  outerContainerRef: HTMLElement | null;
  setOuterContainerRef: (ref: HTMLElement | null) => void;
}) => {
  const addEntryButtonRef = useRef<HTMLButtonElement>(null);
  const scrollToAddedEntryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [innerContainerRef, setInnerContainerRef] = useState<HTMLElement | null>(null);
  const outerContainerSize = useResizeObserver(outerContainerRef, false);
  const innerContainerSize = useResizeObserver(innerContainerRef, false);
  const heightIsLessThanContainer =
    outerContainerSize !== undefined &&
    innerContainerSize !== undefined &&
    innerContainerSize.height <= outerContainerSize.height;
  const widthIsLessThanContainer =
    outerContainerSize === undefined ||
    innerContainerSize === undefined ||
    innerContainerSize.width <= outerContainerSize.width + SCROLL_WIDTH_TOLERANCE_PX;
  const [isConfluencePanelOpen, setIsConfluencePanelOpen] = useState<boolean>(false);
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const renderSection = useScrollPosition({
    isDisabled: !innerContainerRef,
    container: innerContainerRef ?? undefined,
    batchHeight: RENDER_BATCH_HEIGHT,
    margin: RENDER_MARGIN
  });
  const { view } = useView();
  const progressiveEntryRevealAnalyticsSource = getProgressiveEntryRevealAnalyticsSource(
    view.settings.layout.type
  );
  const isTimelineLayout = view.settings.layout.type === DatabaseLayoutType.TIMELINE;
  const isPinnedLeadingColumnsEnabled = getExperimentValue(
    'databasesPinnedLeadingColumns',
    'enabled',
    {
      fireExperimentExposure: false
    }
  );
  const shouldShowTimelineHeaderControls =
    isTimelineLayout &&
    getExperimentValue('databasesRoadmap', 'isEnabled', {
      fireExperimentExposure: false
    });

  useEffect(() => {
    const handleConfluenceSidebarState = async () => {
      const contentAPI = await waitForContentAPI();
      if (contentAPI?.confluenceAPI?.sidebar?.onConfluenceSidebarStateChange) {
        contentAPI.confluenceAPI.sidebar.onConfluenceSidebarStateChange((state) => {
          if (state.isOpen) {
            setIsConfluencePanelOpen(state.isOpen);
          }
        });
      }
    };
    handleConfluenceSidebarState();
  }, []);

  const { left } = useWindowScrollPosition({
    container: innerContainerRef?.parentElement ?? undefined
  });

  const { top } = useScrollPosition({
    isDisabled: !innerContainerRef,
    container: innerContainerRef ?? undefined
  });

  // When an entry is newly created, its main field value should be in editable state and focused.
  // TODO: NCTD-3740
  const [editingIds, setEditingIds] = useState<EditingIds>(new Set());

  const scrollToAddedEntry = useCallback(
    (getScrollLeft: (scrollContainer: HTMLElement) => number) => {
      if (scrollToAddedEntryTimeoutRef.current !== undefined) {
        clearTimeout(scrollToAddedEntryTimeoutRef.current);
      }
      scrollToAddedEntryTimeoutRef.current = setTimeout(() => {
        scrollToAddedEntryTimeoutRef.current = undefined;
        if (outerContainerRef && innerContainerRef) {
          outerContainerRef.scrollTo({
            left: getScrollLeft(outerContainerRef),
            top: innerContainerRef.getBoundingClientRect().height,
            behavior: 'smooth'
          });
        }
      }, 0);
    },
    [innerContainerRef, outerContainerRef]
  );

  useEffect(
    () => () => {
      if (scrollToAddedEntryTimeoutRef.current !== undefined) {
        clearTimeout(scrollToAddedEntryTimeoutRef.current);
      }
    },
    []
  );

  const handleEntry = useCallback(
    (entry: DatabaseEntry) => {
      const isSelectionsPerformanceEnabled = getExperimentValue(
        'databasesSelectionsPerformance',
        'isEnabled',
        { fireExperimentExposure: false }
      );

      if (isSelectionsPerformanceEnabled) {
        // The performance cohort owns editing state via the selections machine — cells open
        // their editors in response to a ping from the machine, not via initialIsEditing.
        // We defer one frame so the new entry row has rendered and its subscribeTo('editing')
        // listener is registered before we notify — notifying before any subscriber exists
        // would silently drop the ping and the editor would never open.
        requestAnimationFrame(() => {
          const selectionsApi = getService('SelectionsApi');
          const { fieldId } = selectionsApi.getValueIdsAtPosition({
            row: { index: 0 },
            col: { index: 0 }
          });
          if (fieldId) {
            selectionsApi.startEditing(entry.id, fieldId, 'table');
          }
        });
      } else {
        setEditingIds(editingIds.add({ entryId: entry.id, fieldId: undefined }));
      }

      scrollToAddedEntry((scrollContainer) =>
        getPinnedLeadingAddEntryScrollLeft({
          currentScrollLeft: scrollContainer.scrollLeft,
          isEnabled: isPinnedLeadingColumnsEnabled,
          isTableLayout: view.settings.layout.type === DatabaseLayoutType.TABLE,
          viewProperties: view.settings.properties
        })
      );
    },
    [
      editingIds,
      isPinnedLeadingColumnsEnabled,
      scrollToAddedEntry,
      view.settings.layout.type,
      view.settings.properties
    ]
  );

  const handleTimelineEntryAdded = useCallback(() => {
    scrollToAddedEntry((scrollContainer) => scrollContainer.scrollLeft);
  }, [scrollToAddedEntry]);

  const content = (
    <DatabaseContentWrapper
      innerRef={setOuterContainerRef}
      heightIsLessThanContainer={heightIsLessThanContainer}
      widthIsLessThanContainer={
        isNativeEmbedEditorPlugin() ? widthIsLessThanContainer : undefined
      }
      allowOverheadSpace={false}
      hideScrollbar={isNativeEmbed()}
      autoScrollAxis={isTimelineLayout ? 'vertical' : undefined}
      isTableLayout={view.settings.layout.type === DatabaseLayoutType.TABLE}
    >
      <div
        className={`${styles.databaseContentInnerContainer} ${canEditContent ? styles.canEditContent : ''}`}
        id="database-content-inner-container"
        data-testid={'database-content-inner-container'}
        ref={setInnerContainerRef}
      >
        {isDBAiFirstCreationEnabled && outerContainerRef && (
          <InlineRovoPrompt container={outerContainerRef} />
        )}
        {progressiveEntryRevealAnalyticsSource ? (
          <ProgressiveEntryRevealOwner
            analyticsSource={progressiveEntryRevealAnalyticsSource}
            contentId={contentId}
            editingIds={editingIds}
            viewId={view.id}
            viewSettings={view.settings}
          >
            <LayoutResizeObserver>
              {view.settings.layout.type === DatabaseLayoutType.GALLERY ? (
                outerContainerSize && (
                  <Suspense fallback={<DatabaseGallerySkeleton />}>
                    <GalleryLayout
                      canEditContent={canEditContent}
                      parentContainerRef={innerContainerRef}
                      containerWidth={outerContainerSize.width}
                    />
                  </Suspense>
                )
              ) : view.settings.layout.type === DatabaseLayoutType.BOARD ? (
                outerContainerSize && (
                  <Suspense fallback={<DatabaseBoardSkeleton />}>
                    <BoardLayout
                      canEditFields={canEditFields}
                      canEditContent={canEditContent}
                      containerWidth={outerContainerSize.width}
                      containerRef={innerContainerRef}
                    />
                  </Suspense>
                )
              ) : isTimelineLayout ? (
                <Suspense fallback={<DatabaseTableSkeleton />}>
                  <ProgressiveTimelineLayout
                    contentId={contentId}
                    canEditContent={canEditContent}
                    canEditFields={canEditFields}
                    onEntryAdded={handleTimelineEntryAdded}
                    renderSection={renderSection}
                    scrollContainer={outerContainerRef}
                    scrollContainerWidth={outerContainerRef?.clientWidth}
                    shouldShowTimelineHeaderControls={shouldShowTimelineHeaderControls}
                  />
                </Suspense>
              ) : (
                outerContainerSize && (
                  <Suspense fallback={<DatabaseTableSkeletonRefresh />}>
                    <TableLayout
                      editingIds={editingIds}
                      setEditingIds={setEditingIds}
                      contentId={contentId}
                      isContentEditable={isContentEditable}
                      canEditContent={canEditContent}
                      canEditFields={canEditFields}
                      couldEditFields={couldEditFields}
                      renderSection={renderSection}
                      containerWidth={outerContainerRef?.clientWidth ?? 0}
                      addEntryButtonRef={addEntryButtonRef}
                      containerTop={top}
                      containerLeft={left}
                      handleEntry={handleEntry}
                      isConfluencePanelOpen={isConfluencePanelOpen}
                    />
                  </Suspense>
                )
              )}
            </LayoutResizeObserver>
          </ProgressiveEntryRevealOwner>
        ) : (
          <LayoutResizeObserver>
            {outerContainerSize && (
              <Suspense fallback={<DatabaseTableSkeleton />}>
                <CalendarLayout
                  canEditFields={canEditFields}
                  canEditContent={canEditContent}
                  contentId={contentId}
                  renderSection={renderSection}
                  containerWidth={outerContainerRef?.clientWidth ?? 0}
                />
              </Suspense>
            )}
          </LayoutResizeObserver>
        )}
      </div>
    </DatabaseContentWrapper>
  );

  return (
    <DatabaseAiConfigModalProvider contentId={contentId}>
      {isNativeEmbedEditorPlugin() ? (
        <NativeEmbedSelectionBorder>{content}</NativeEmbedSelectionBorder>
      ) : (
        content
      )}
    </DatabaseAiConfigModalProvider>
  );
};

export default DatabaseLayoutRenderer;
